<template>
    <v-card class="pa-3 kim-card">

        <template v-if="userIsOphthalmologist">

            <v-card-text class="text-left pt-3 pb-1">
                <v-icon color="grey" class="mr-1">fas fa-glasses</v-icon>
                Lentes:
            </v-card-text>

            <v-card-text class="py-1">
                <glasses-form
                    :readonly="readonly"
                    :event-id="eventId"
                    @update="onGlassesUpdate"
                    :glasses="indication && indication.glasses || null" />
            </v-card-text>

        </template>

        <v-divider></v-divider>

        <v-card-text class="text-left pt-3 pb-1">
            <v-icon color="grey" class="mr-1">fas fa-pills</v-icon>
            Medicamentos:
        </v-card-text>

        <v-card-text class="py-1">

            <medicines-picker
                    v-if="!readonly"
                    label="Buscar medicamento"
                    :event-id="eventId"
                    @change="onMedicineSelect"
            />

            <template v-if="medicines.length === 0">
                <v-card-text class="text-center">
                    <h1 class="caption">No se han indicado medicamentos</h1>
                </v-card-text>
            </template>

            <template v-else>
                <v-card-text class="py-3" >

                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                      <tr>
                        <th scope="col" class="text-left">Medicamento</th>
                        <th scope="col" class="text-center">Frecuencia</th>
                        <th scope="col" class="text-center">Durante</th>
                        <th scope="col" class="text-center">Nota</th>
                        <th scope="col" class="text-center">Eliminar</th>
                      </tr>
                      </thead>
                      <tbody>
                        <tr
                            v-for="(item) in medicines"
                            :key="item.pivot.id">
                          <td class="text-left">{{ item.name }}</td>
                          <td class="text-center">cada {{ item.pivot.interval }} hora{{ item.pivot.interval > 1 ? 's' : ''}}</td>
                          <td class="text-center">{{ item.pivot.duration }} {{medicineIntervalUnits[item.pivot.duration_unit]}}</td>
                          <td class="text-center">
                            {{ item.pivot.comment || '-'}}
                          </td>
                          <td class="text-center">
                            <v-icon v-if="!readonly" color="accent" @click="deleteMedicine(item)">delete</v-icon>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>

                </v-card-text>

            </template>

        </v-card-text>

        <v-divider></v-divider>

        <v-card-text class="text-left pt-3 pb-1">
            <v-icon color="grey" class="mr-1">fas fa-folder-plus</v-icon>
            Exámenes complementarios:
        </v-card-text>

        <v-card-text class="pt-1 pb-0">
            <complementary-exam-picker
                    v-if="!readonly"
                    label="Buscar exámen"
                    :event-id="eventId"
                    @change="onExamSelect"
                    />
        </v-card-text>

        <template v-if="exams.length === 0">
            <v-card-text class="text-center">
                <h1 class="caption">No se han indicado exámenes complementarios</h1>
            </v-card-text>
        </template>

        <v-card-text v-else class="pt-0">
            <v-chip class="ma-1" v-for="exam in exams" :key="exam.id">
                {{exam.description}}
                <v-icon
                    @click="deleteExam(exam)"
                    color="accent"
                    v-if="!readonly"
                    right>mdi-close</v-icon>
            </v-chip>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-text class="text-left pt-3 pb-1">
            <v-icon color="grey" class="mr-1">fas fa-stethoscope</v-icon>
            Control:
        </v-card-text>

        <v-card-text class="py-1">
            <v-flex xs12 sm6>
                <control-form
                        :event-id="eventId"
                        :readonly="readonly"
                        :control="indication && indication.control || null"
                        @update="onControlUpdate"
                />
            </v-flex>
        </v-card-text>


        <medicine-form ref="medicineForm" :event-id="eventId" @attach="onMedicineAttach"/>

        <kimsa-confirm
                ref="deleteMedicineModal"
                title="Eliminar medicamento"
                content="¿Está seguro que desea eliminar este ítem?"
                :loading="loadingMedicineDelete"
                :persistent="false"
                @confirm="deleteMedicineConfirm"
                :close-on-confirm="false"
        ></kimsa-confirm>

        <kimsa-confirm
                ref="deleteExamModal"
                title="Eliminar exámen"
                content="¿Está seguro que desea eliminar este ítem?"
                :loading="loadingExamDelete"
                :persistent="false"
                @confirm="deleteExamConfirm"
                :close-on-confirm="false"
        ></kimsa-confirm>

    </v-card>
</template>

<script>
    import KimsaConfirm from '@/components/common/KimsaConfirm'
    import MedicinesPicker from './MedicinesPicker'
    import ComplementaryExamPicker from './ComplementaryExamPicker'
    import GlassesForm from './indications/GlassesForm'
    import ControlForm from './indications/ControlForm'
    import MedicineForm from './indications/MedicineForm.vue'

    import {debounce} from '@/utils/Common'
    import {ApiUtils} from '@/utils/ApiUtils'
    import {userProvides, OPHTHALMOLOGY} from '@/utils/Specialist'

    import {mapState, mapMutations} from 'vuex'


    export default{
        props: ['readonly', 'eventId', 'indication'],
        components:{
            MedicinesPicker,
            KimsaConfirm,
            ComplementaryExamPicker,
            GlassesForm,
            ControlForm,
            MedicineForm
        },
        data(vm){
            return {

                loadingMedicineAttach: false,
                loadingMedicineDelete: false,

                loadingExamAttach: false,
                loadingExamDelete: false,

                updatingGlasses: false,

                medicineIntervalUnits: {
                    days: 'día(s)',
                    weeks: 'semana(s)',
                    months: 'mes(es)'
                },

                medicines: vm.indication && vm.indication.medicines || [],
                medicineToDetach: null,

                exams: vm.indication && vm.indication.complementary_exams || [],
                examToDetach: null,

                paginationOptions: {
                    sortDesc: true,
                    page: 1,
                    itemsPerPage: -1, // -1 for All",
                    sortBy: ["pivot.id"],
                    totalItems: 1000
                },

                medicineHeaders: [
                    {
                        text: 'Medicamento',
                        align: 'left',
                        sortable: false,
                        value: 'pivot.name'
                    },
                    { text: 'Frecuencia', value: 'pivot.interval',   align: 'center', sortable: true,},
                    { text: 'Durante', value: 'pivot.duration',   align: 'center', },
                    { text: 'Nota', value: 'pivot.comment',   align: 'center', },
                    { text: 'Eliminar',  align: 'center', },
                ]

            }
        },

        methods: {

            ...mapMutations(['showSnackBar']),
            onControlUpdate(control){
                if(control.required === "0"){
                    control.required = 0
                }
              this.$emit('update:control', control)
            },
            onGlassesUpdate(glasses){
              this.$emit('update:glasses', glasses)
            },

            onMedicineAttach(medicine){
                var vm = this
                vm.$emit('attach:medicine', medicine)
            },
            async onMedicineSelect(medicine){

                var vm = this
                if (!medicine) return

                vm.$refs.medicineForm.show(medicine)

            },
            async onExamSelect(exam){

                var vm = this
                if (!exam) return

                vm.loadingExamAttach = true
                const response = await ApiUtils.makePost(`indications/complementary-exam/attach`, {
                    schedule_event_id: vm.eventId,
                    service_item_id: exam.id
                })
                vm.loadingExamAttach = false

                if (response.ok) {
                    const json = await response.json()
                    const attachedItem = json.result.exam
                    vm.$emit('attach:exam', attachedItem)
                }
            },
            deleteMedicine(medicine){
                var vm = this
                vm.medicineToDetach = medicine
                vm.$refs.deleteMedicineModal.show()
            },
            deleteExam(exam){
                var vm = this
                vm.examToDetach = exam
                vm.$refs.deleteExamModal.show()
            },
            async deleteMedicineConfirm(){
                var vm = this

                if(vm.loadingMedicineDelete || !vm.currentProvider) return

                if(!vm.medicineToDetach) {
                    throw new Error('Item to delete not set')
                }

                vm.loadingMedicineDelete = true
                const response = await ApiUtils.makePost(`indications/medicine/detach`, {
                    medicine_id: vm.medicineToDetach.id,
                    schedule_event_id: vm.eventId,
                })
                vm.loadingMedicineDelete = false

                if(!response.ok){
                    const json = await response.json()
                    vm.showSnackBar(json.message || 'Ha ocurrido un error intentando eliminar el item')
                    return
                }

               vm.$emit('detach:medicine', {
                   id: vm.medicineToDetach.id
               })
                vm.medicineToDetach = null
                vm.$refs.deleteMedicineModal.hide()
            },
            async deleteExamConfirm(){
                var vm = this

                if(vm.loadingExamDelete || !vm.currentProvider) return

                if(!vm.examToDetach) {
                    throw new Error('Item to delete not set')
                }

                vm.loadingExamDelete = true
                const response = await ApiUtils.makePost(`indications/complementary-exam/detach`, {
                    service_item_id: vm.examToDetach.id,
                    schedule_event_id: vm.eventId,
                })
                vm.loadingExamDelete = false

                if(!response.ok){
                    const json = await response.json()
                    vm.showSnackBar(json.message || 'Ha ocurrido un error intentando eliminar el item')
                    return
                }

                vm.$emit('detach:exam', {
                    id: vm.examToDetach.id
                })

                vm.examToDetach = null
                vm.$refs.deleteExamModal.hide()

            },
        },
        computed: {
            ...mapState(['currentProvider']),
            userIsOphthalmologist(){
                return userProvides(OPHTHALMOLOGY)
            }

        },
        watch: {
            clientMedicines(val){
                this.medicines = val
            },
            indication(val){
                var vm = this
                vm.medicines = val && val.medicines || []
                vm.exams = val && val.complementary_exams || []
            }

        }

    }



</script>
